/* eslint-disable prettier/prettier */
import { toNumber } from "./utils";

// Two members of the same group can book expenses in the same month. Both amounts
// are per-attorney figures entered on the Expenses page, so the group's row is
// their sum — keeping only one member's entry under-reports the group.
// Amounts are normalised to numbers whether or not a merge happened, so grouped
// expenses never carry a mix of "100" and 100 for the same field.
const mergeExpenseEntries = (existing, incoming) => {
    const direct = toNumber(incoming.AttorneyDirectExpenses);
    const general = toNumber(incoming.TotalGeneralExpenses);

    if (!existing) {
        return { ...incoming, AttorneyDirectExpenses: direct, TotalGeneralExpenses: general };
    }

    return {
        ...existing,
        AttorneyDirectExpenses: toNumber(existing.AttorneyDirectExpenses) + direct,
        TotalGeneralExpenses: toNumber(existing.TotalGeneralExpenses) + general,
    };
};

export let getGrouping = (
    groups,
    runOption,
    clioMatters,
    persistedKeyedData,
    clioActivitiesKeyed,
    clioActivitiesAttorneyMatters
) => {
    const groupsreturned = {};



    const kgroup = {};
    groups.forEach((group) => {
        group.attorneys.forEach((attorney) => {
            kgroup[attorney] = group.id;
        });
    });

    const groupMapping = (attorney) => {
        return runOption === "all_groups" ||
            runOption === "select_group"
            ? attorney in kgroup
                ? kgroup[attorney]
                : attorney
            : attorney;
    };

    //map to groups
    groupsreturned.clioMatters = clioMatters.map((item) => {
        return {
            ...item,
            originating_attorney: {
                ...item.originating_attorney,
                name: groupMapping(item.originating_attorney.name),
            },
            responsible_attorney: {
                ...item.responsible_attorney,
                name: groupMapping(item.responsible_attorney.name),
            },
        };
    });
    groupsreturned.persistedKeyedData = {
        ...persistedKeyedData,
        // Merge each attorney's expenses into their group rather than replacing:
        // the previous version keyed the reduce by group id and spread only the
        // accumulator, so the last member of a group overwrote every earlier one.
        expenses: Object.entries(persistedKeyedData.expenses || {}).reduce((a, b) => {
            const groupKey = groupMapping(b[0]);
            const remapped = Object.entries(b[1]).reduce((c, d) => {
                return {
                    ...c,
                    [d[0]]: { ...d[1], attorney: groupMapping(d[1].attorney) },
                };
            }, {});

            const merged = { ...(a[groupKey] || {}) };
            Object.entries(remapped).forEach(([date, entry]) => {
                merged[date] = mergeExpenseEntries(merged[date], entry);
            });

            return { ...a, [groupKey]: merged };
        }, {}),
    };

    groupsreturned.clioActivitiesKeyed = Object.entries(clioActivitiesKeyed).reduce((a, b) => {
        return {
            ...a,
            [b[0]]: Object.entries(b[1]).reduce((c, d) => {
                return {
                    ...c,
                    [groupMapping(d[0])]: groupMapping(d[0]) in c ? c[groupMapping(d[0])] + d[1] : d[1],
                };
            }, {}),
        };
    }, {});

    // Always a flat Set of display numbers. The previous version assigned a Set for
    // the first attorney of a group but then built `[...set, nextSet]` for each
    // subsequent one, nesting whole Sets as array elements. Consumers do
    // `Array.from(x).includes(displayNumber)` (matters.js, payments.js), which
    // silently returned false for members 2..N — dropping their matters from both
    // sheets instead of throwing.
    const groupedClioActivitiesAttorneyMatters = {};
    Object.entries(clioActivitiesAttorneyMatters).forEach((item) => {
        const attny = groupMapping(item[0]);
        const existing = groupedClioActivitiesAttorneyMatters[attny];

        groupedClioActivitiesAttorneyMatters[attny] = existing
            ? new Set([...existing, ...item[1]])
            : new Set(item[1]);
    });

    groupsreturned.clioActivitiesAttorneyMatters = groupedClioActivitiesAttorneyMatters;

    return groupsreturned;
};
